import React from 'react';
import { motion } from 'framer-motion';
import { Search, Zap, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';

interface SolutionSectionProps {
  companyName?: string;
}

export const SolutionSection: React.FC<SolutionSectionProps> = ({ 
  companyName = "votre entreprise" 
}) => {
  const methodology = [
    {
      step: "01",
      icon: Search,
      title: "Audit Complet",
      description: "Analyse technique approfondie de votre site : performance, SEO, UX, concurrence.",
      details: [
        "Audit technique 360°",
        "Analyse concurrentielle",
        "Identification des blocages",
        "Opportunités de croissance"
      ],
      duration: "48h"
    },
    {
      step: "02",
      icon: Zap,
      title: "Optimisation",
      description: "Mise en œuvre des corrections prioritaires pour des résultats rapides et mesurables.",
      details: [
        "Optimisation vitesse",
        "Amélioration SEO technique",
        "Optimisation conversions",
        "Content marketing"
      ],
      duration: "30 jours"
    },
    {
      step: "03",
      icon: TrendingUp,
      title: "Croissance",
      description: "Stratégie long terme pour multiplier votre trafic et vos conversions de façon durable.",
      details: [
        "Stratégie de contenu",
        "Link building premium",
        "Optimisation continue",
        "Reporting mensuel"
      ],
      duration: "90 jours"
    }
  ];

  const results = [
    { metric: "+287%", label: "Trafic organique moyen", color: "text-orange-400" },
    { metric: "+156%", label: "Taux de conversion", color: "text-green-400" },
    { metric: "+94%", label: "Vitesse de chargement", color: "text-blue-400" },
    { metric: "90 jours", label: "Pour voir les résultats", color: "text-purple-400" }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-slate-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-400/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-orange-500/3 to-gray-700/5 rounded-full blur-3xl"></div>
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.02%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-500/20 border border-orange-500/30 text-orange-400 text-sm font-medium mb-6">
              <Zap className="w-4 h-4 mr-2" />
              Notre Méthodologie
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Comment nous multiplions{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">
                votre trafic par 3
              </span>
            </h2>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Notre approche éprouvée en 3 étapes pour transformer {companyName} en machine à générer des leads qualifiés.
            </p>
          </motion.div>

          {/* Methodology Steps */}
          <div className="grid lg:grid-cols-3 gap-8 mb-20">
            {methodology.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="group relative"
              >
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700/50 hover:border-orange-500/30 transition-all duration-300 transform hover:-translate-y-2 h-full">
                  
                  {/* Step Number */}
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    {step.step}
                  </div>

                  {/* Step Icon */}
                  <div className="mb-6 pt-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-100/10 to-orange-200/10 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <step.icon className="w-8 h-8 text-orange-400" />
                    </div>
                  </div>

                  {/* Step Content */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-bold text-white">
                        {step.title}
                      </h3>
                      <span className="text-orange-400 text-sm font-semibold bg-orange-500/20 px-2 py-1 rounded-full">
                        {step.duration}
                      </span>
                    </div>
                    <p className="text-gray-300 leading-relaxed mb-6">
                      {step.description}
                    </p>
                  </div>

                  {/* Step Details */}
                  <div className="space-y-2">
                    {step.details.map((detail, detailIndex) => (
                      <div key={detailIndex} className="flex items-center text-gray-400 text-sm">
                        <CheckCircle className="w-4 h-4 text-orange-400 mr-2 flex-shrink-0" />
                        <span>{detail}</span>
                      </div>
                    ))}
                  </div>

                  {/* Connection Arrow */}
                  {index < methodology.length - 1 && (
                    <div className="hidden lg:block absolute -right-4 top-1/2 transform -translate-y-1/2 text-orange-400">
                      <ArrowRight className="w-8 h-8" />
                    </div>
                  )}

                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Results Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-8 md:p-12 border border-gray-700/50 relative overflow-hidden"
          >
            {/* Background Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-2xl"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-orange-400/5 rounded-full blur-2xl"></div>

            <div className="relative z-10">
              <div className="text-center mb-12">
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30 text-green-400 text-sm font-medium mb-6">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Résultats Garantis
                </div>

                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Des résultats qui parlent d'eux-mêmes
                </h3>

                <p className="text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed">
                  Nos clients obtiennent en moyenne ces résultats impressionnants avec notre méthodologie éprouvée.
                </p>
              </div>

              {/* Results Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {results.map((result, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center group"
                  >
                    <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/30 group-hover:border-orange-500/30 transition-all duration-300">
                      <div className={`text-3xl md:text-4xl font-bold mb-2 ${result.color}`}>
                        {result.metric}
                      </div>
                      <div className="text-gray-400 text-sm leading-tight">
                        {result.label}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Guarantee Badge */}
              <div className="text-center mt-12">
                <div className="inline-flex items-center px-6 py-3 rounded-full bg-orange-500/20 border border-orange-500/30 text-orange-400 font-semibold">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Résultats garantis ou remboursé
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
